import { BodyText } from '@lmig/lmds-react-typography';
import { Link } from '@lmig/lmds-react-link';
import { useState, useEffect } from 'react';
import TableColumnHeader from './TableColumnHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudDownloadAlt, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import RecentActivitiesFilterWindow from './RecentActivitiesFilterWindow';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { GetRecentActivities } from './RecentActivitiesAPI';
import { FilterDates, GetResultData } from './RecentActivitiesHelper';
import AccessDeniedMessage from '../Helpers/AccessDeniedMessage';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import { deleteSessionStorage, useSessionStorage } from 'Utilities/UseStorage';
import { useUserData } from 'Foundation/User/UserContext';
import { Notification } from '@lmig/lmds-react-notification';
import './RecentActivities.scss';

interface RASessionData {
  dataTable: [];
  agencyCode: string;
  noResultsMessage: string;
  responseStatus: number;
}

function RecentActivitiesTable(props: any) {
  const expirationMinutes: number = 8 * 60;
  const RA_KEY = 'RecentActivites';
  const { userData } = useUserData();
  const [responseStatus, setResponseStatus] = useState<{ statusCode: number; message: string }>({
    statusCode: HttpStatusCodes.InternalServerError,
    message: '',
  });
  const [sessionData, setSessionData] = useSessionStorage<RASessionData>(
    getSessionKey(),
    {
      dataTable: [],
      agencyCode: '',
      noResultsMessage: '',
      responseStatus: HttpStatusCodes.InternalServerError,
    } as RASessionData,
    expirationMinutes,
  );
  const [table, setTable] = useState<{
    isLoaded: boolean;
    sortedItems: any[];
    filteredOptions: any[];
    hideDropDown: boolean;
    items: any[];
  }>({
    isLoaded: false,
    sortedItems: [],
    filteredOptions: [],
    hideDropDown: true,
    items: [],
  });
  const defaultMessage = 'There are no recent activities';
  const errorMessage = `There was an error trying to get ${props.activityDataType} table data. Try again later.`;

  function getSessionKey() {
    return `${RA_KEY}-${props.activityDataType}`;
  }

  useEffect(() => {
    if (isSessionDataInvalid()) {
      deleteSessionStorage(getSessionKey());
    }

    if (!table.isLoaded) getItems();

    //Return functions in useEffect execute on unmount process.
    //Exists to handle memory leak messages, cleans state on unmount.
    return () => {
      setResponseStatus({ statusCode: HttpStatusCodes.InternalServerError, message: '' });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function sortItems(columnToSort: any, activityDataType: string, results: any[] | null, shouldToggle: boolean) {
    var newRows = props.getSortedItemsCallBack(columnToSort, activityDataType, results, shouldToggle);
    setTable((prevState) => ({ ...prevState, sortedItems: newRows }));
  }

  function isEmployee() {
    if (!userData) return false;
    return !userData.isAgent();
  }

  function isSessionDataInvalid() {
    return (
      !sessionData ||
      (isEmployee() && sessionData?.agencyCode !== props.agencyCode) ||
      (isStatusError(sessionData?.responseStatus) && sessionData.dataTable.length === 0)
    );
  }

  async function getItems() {
    try {
      if (!userData) return;

      let result: any = [];
      let response: IServerResponse = {
        Status: { Code: HttpStatusCodes.InternalServerError, Message: '' },
        Data: null,
      };
      if (isSessionDataInvalid()) {
        response = await GetRecentActivities(props.activityDataType, props.agencyCode);
        if (response.Status.Code === HttpStatusCodes.OK) result = GetResultData(response, props.activityDataType);
        else result = [];
        setResponseStatus({ statusCode: response.Status.Code, message: response.Status.Message });
      } else {
        result = sessionData?.dataTable;
        sessionData &&
          setResponseStatus({
            statusCode: sessionData.responseStatus,
            message: sessionData.noResultsMessage,
          });
      }
      if (result !== '') {
        //format result based on time range for respective table
        result = FilterDates(result, props.activityDataType);
        setTable((prevState) => ({
          ...prevState,
          isLoaded: true,
          items: result,
        }));
        if (isSessionDataInvalid() && !isStatusError(response.Status.Code)) {
          let agencyCode = props.agencyCode.toString();
          setSessionData({
            data: {
              dataTable: result,
              agencyCode: agencyCode,
              noResultsMessage: response?.Status?.Message,
              responseStatus: response?.Status?.Code,
            } as RASessionData,
          });
        }
        props.dataCallBack(result, props.activityDataType);
        // sort items with default sort field
        sortItems('ActivityDate', props.activityDataType, result, true);
      }
    } catch (error) {
      console.error(error);
    }
  }

  function toggleShowFilterDropDown() {
    setTable((prevState) => ({
      ...prevState,
      hideDropDown: !prevState.hideDropDown,
    }));
  }

  function updateView(filteredResults: any[], updatedFilteredOptions: any[]) {
    setTable((prevState) => ({
      ...prevState,
      sortedItems: filteredResults,
      filteredOptions: updatedFilteredOptions,
    }));
    props.dataCallBack(filteredResults, props.activityDataType);

    let prevSortedColumn = props.sortedColumn.column;
    sortItems(prevSortedColumn, props.activityDataType, filteredResults, false);
  }

  function exportData() {
    props.exportData();
  }

  const isStatusError = (statusCode: number) => {
    //checks for error that isn't unauthorized since accessdenied component handles that
    return statusCode !== HttpStatusCodes.OK && statusCode !== HttpStatusCodes.Forbidden;
  };

  return (
    <>
      {userData && !table.isLoaded ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgressSpinner size="32" aria-label="Loading recent activities data..." />
        </div>
      ) : !userData?.selectedStatCodeData?.SubStat ? (
        <Notification alert="Please try again later. " highlightType="negative">
          The data for this agency/policy is temporarily unavailable. If this issue persists, please contact Agency
          Interface Support Group at 888-451-8414.
        </Notification>
      ) : (
        <div>
          <div style={{ paddingBottom: '1.5em', display: 'flex', justifyContent: 'space-between' }}>
            <div className="table-controls">
              <div
                id={props.activityDataType + '-filter-dropdown'}
                className="filter-dropdown"
                onClick={toggleShowFilterDropDown}
              >
                Filter
                <FontAwesomeIcon className="filter-dropdown-icon" icon={faCaretDown as IconProp} />
              </div>
              <Link style={{ marginLeft: '10px' }} onClick={exportData} disabled={!props.isParentLoaded}>
                <FontAwesomeIcon className="export-icon" icon={faCloudDownloadAlt as IconProp} />
                Export
              </Link>
            </div>
          </div>
          <RecentActivitiesFilterWindow
            toggleVisibility={toggleShowFilterDropDown}
            items={table.items}
            columns={props.columns}
            updateView={updateView}
            activityDataType={props.activityDataType}
            hideDropDown={table.hideDropDown}
            filteredOptions={table.filteredOptions}
          ></RecentActivitiesFilterWindow>
          <TableColumnHeader
            columns={props.columns}
            sortingFn={sortItems}
            sortedColumn={props.sortedColumn}
            sortedItems={table.sortedItems}
            activityDataType={props.activityDataType}
            link={props.link}
          />
          {table.sortedItems.length === 0 && table.isLoaded && table.filteredOptions.length > 0 ? (
            <BodyText type="article" className="no-items-text">
              <p>The filters applied returned no results. Please update the filters and try again.</p>
            </BodyText>
          ) : table.sortedItems.length === 0 && table.isLoaded ? (
            <BodyText type="article" className="no-items-text">
              {isStatusError(responseStatus.statusCode) ? (
                errorMessage
              ) : responseStatus.statusCode === HttpStatusCodes.OK ? (
                defaultMessage
              ) : (
                <AccessDeniedMessage
                  status={responseStatus.statusCode}
                  responseMessage={responseStatus.message}
                  activityDataType={props.activityDataType}
                ></AccessDeniedMessage>
              )}
            </BodyText>
          ) : (
            ''
          )}
        </div>
      )}
    </>
  );
}

export default RecentActivitiesTable;
